import { useEffect, useState } from 'react';

import { useAtom } from 'jotai';
import { useLDClient } from 'launchdarkly-react-client-sdk';

import useAuth from '@/hooks/useAuth';
import { sendNativeMessage } from '@/hooks/useNativeListener';

import { getLaunchdDarklyUser } from '@/utils/user';

import { appInitializedAtom } from './state';

const LaunchDarklySetup: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { data: auth } = useAuth();
  const [isInitialized] = useAtom(appInitializedAtom);
  const ldClient = useLDClient();
  const isInternational = auth?.isInternationalUser;
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!ldClient || !auth || !isInitialized) return;

    const logDevAllFlags = (label: string) => {
      if (import.meta.env.DEV) {
        console.log(`[LaunchDarkly] ${label}`, ldClient.allFlags());
      }
    };
    const onFlagsChanged = () => {
      if (!cancelled) logDevAllFlags('flags updated (identify or stream)');
    };
    if (import.meta.env.DEV) ldClient.on('change', onFlagsChanged);

    const markReady = (label: string) => {
      if (cancelled) return;
      logDevAllFlags(label);
      setReady(true);
    };

    void (async () => {
      const ldUser = getLaunchdDarklyUser(auth);
      if (isInternational) {
        await ldClient.identify(ldUser);
        markReady('flags after identify (international)');
        return;
      }
      sendNativeMessage({ type: 'updateUser', payload: { user: auth } });
      if (ldUser.email) await ldClient.identify(ldUser);
      markReady(
        ldUser.email
          ? 'flags after identify'
          : 'flags (still anonymous context; user has no email)',
      );
    })();

    return () => {
      cancelled = true;
      if (import.meta.env.DEV) ldClient.off('change', onFlagsChanged);
    };
  }, [isInitialized, isInternational, ldClient, auth]);

  return ready ? children : null;
};

export default LaunchDarklySetup;
